import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FixedSizeGrid, GridChildComponentProps } from 'react-window';
import { Chip, Box, Typography } from '@mui/material';

interface VirtualizedChipListProps {
  items: string[];
  onDeleteItem: (item: string) => void;
  columnWidth?: number;
  rowHeight?: number;
  listHeight?: number;
}

const VirtualizedChipList: React.FC<VirtualizedChipListProps> = ({ 
  items, 
  onDeleteItem,
  columnWidth = 150,
  rowHeight = 36,
  listHeight = 200 
}) => {
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState(3);
  const [gridWidth, setGridWidth] = useState(0);

  const updateDimensions = useCallback(() => {
    if (gridContainerRef.current) {
      const containerWidth = gridContainerRef.current.clientWidth;
      const newColumnCount = Math.floor(containerWidth / columnWidth);
      setColumnCount(Math.max(1, newColumnCount));
      setGridWidth(containerWidth);
    }
  }, [columnWidth]);

  useEffect(() => {
    updateDimensions();
  }, [updateDimensions, items.length]);

  // This effect recalculates on window resize
  useEffect(() => {
    updateDimensions(); // Initial call
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [updateDimensions]);

  if (items.length === 0) {
    return (
      <Box sx={{ height: listHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No codes selected.
        </Typography>
      </Box>
    );
  }

  const rowCount = Math.ceil(items.length / columnCount);

  const Cell = ({ columnIndex, rowIndex, style }: GridChildComponentProps) => {
    const index = rowIndex * columnCount + columnIndex;
    if (index >= items.length) {
      return null;
    }
    const item = items[index];

    return (
      <Box style={style} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: '2px 4px' }}>
        <Chip
          label={item}
          onDelete={() => onDeleteItem(item)}
          size="small"
          sx={{ width: '95%' }}
        />
      </Box>
    );
  };

  // Cast to a generic component type to work around a known typings
  // incompatibility between @types/react-window and certain @types/react versions.
  const GridComponent = FixedSizeGrid as unknown as React.ComponentType<any>;

  return (
    <Box ref={gridContainerRef} sx={{ width: '100%', height: listHeight }}>
      {gridWidth > 0 ? (
          <GridComponent
          key={`grid-${items.length}-${columnCount}`}
          height={listHeight}
          width={gridWidth}
          columnCount={columnCount}
          columnWidth={columnWidth}
          rowCount={rowCount}
          rowHeight={rowHeight}
          overscanRowCount={5}
        >
          {Cell}
        </GridComponent>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1 }}>
          {items.map((item: string) => (
            <Chip
              key={item}
              label={item}
              onDelete={() => onDeleteItem(item)}
              size="small"
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default VirtualizedChipList; 